import React, { createContext, useContext } from 'react'
import { Tag } from '@surecompanies/core_components'
import { Pencil } from 'lucide-react'

import { BenefitRow } from '../../../api/reconciliation-v2-api-client'
import { getPrimaryPaymentMethodTagColor } from '../../VcnManagement/VcnManagementTableCellTags'
import { colors, radius, space } from '../tokens'

/**
 * The benefit's primary payment method as a tag, like Payment Assignment's
 * column, with a pencil that opens Payment Assignment's payment method section
 * for users who may write reconciliation — also when no method is set yet.
 * `INFO_COLUMNS` renders from the row alone, so the open handler reaches the
 * cell through context; null hides the pencil.
 */
export const PrimaryPaymentMethodEditContext = createContext<
  ((row: BenefitRow) => void) | null
>(null)

const PrimaryPaymentMethodCell: React.FC<{ row: BenefitRow }> = ({ row }) => {
  const onEdit = useContext(PrimaryPaymentMethodEditContext)
  const method = row.primaryPaymentMethod?.trim() ?? ''
  // The section edits through a payment assignment, so a benefit without one
  // has nothing to open.
  const canEdit = onEdit !== null && row.paymentAssignmentId != null

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}
    >
      {method ? (
        <Tag color={getPrimaryPaymentMethodTagColor(method)}>{method}</Tag>
      ) : (
        <span style={{ color: colors.text.disabled }}>—</span>
      )}
      {canEdit ? (
        <button
          type="button"
          title="Edit primary payment method"
          aria-label="Edit primary payment method"
          onClick={() => onEdit(row)}
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 20,
            height: 20,
            padding: 0,
            border: 'none',
            borderRadius: radius.sm,
            background: 'transparent',
            color: colors.text.placeholder,
            cursor: 'pointer',
          }}
        >
          <Pencil size={12} aria-hidden />
        </button>
      ) : null}
    </span>
  )
}

export default PrimaryPaymentMethodCell
